// Offline Storage Service - Duolingo-style caching
// Uses IndexedDB to cache questions for offline support
// IMPORTANT: Caches the question POOL, not filtered selections
// Adaptive filtering/personalization happens AFTER loading from cache

import { openDB, DBSchema, IDBPDatabase } from 'idb';
import type { Question } from '../types';
import { logger } from '../utils/logger';

interface QuestionsDB extends DBSchema {
  questions: {
    key: string; // `${testId}-${language}` for pools, `question-${questionId}-${language}` for individual
    value: {
      testId: string;
      language: string;
      questions: Question[];
      timestamp: number;
    };
    indexes: { 'by-test': string; 'by-language': string };
  };
  // Individual question cache for dynamic mock exams
  questionCache: {
    key: string; // `question-${questionId}-${language}`
    value: {
      questionId: string;
      language: string;
      question: Question;
      timestamp: number;
    };
    indexes: { 'by-question': string; 'by-language': string };
  };
}

class OfflineStorageService {
  private db: IDBPDatabase<QuestionsDB> | null = null;
  private readonly DB_NAME = 'theory-ai-db';
  private readonly DB_VERSION = 2; // Incremented to add questionCache store
  private readonly CACHE_EXPIRY_DAYS = 30; // Cache for 30 days
  private initRetryCount = 0;
  private readonly MAX_INIT_RETRIES = 3;

  async init(): Promise<void> {
    if (this.db) return;
    if (this.initRetryCount >= this.MAX_INIT_RETRIES) return;

    try {
      this.db = await openDB<QuestionsDB>(this.DB_NAME, this.DB_VERSION, {
        upgrade(db, _oldVersion) {
          // Create questions store (for question pools)
          if (!db.objectStoreNames.contains('questions')) {
            const store = db.createObjectStore('questions', { keyPath: 'key' });
            store.createIndex('by-test', 'testId');
            store.createIndex('by-language', 'language');
          }
          
          // Create questionCache store (for individual questions in dynamic exams)
          if (!db.objectStoreNames.contains('questionCache')) {
            const store = db.createObjectStore('questionCache', { keyPath: 'key' });
            store.createIndex('by-question', 'questionId');
            store.createIndex('by-language', 'language');
          }
        },
        blocked() {
          // Database is blocked by another connection - wait and retry
          logger.warn('IndexedDB is blocked by another connection');
        }
      });
      this.initRetryCount = 0;
      logger.debug('IndexedDB initialized successfully');
    } catch (error: any) {
      // Handle "Database deleted by request of the user" error gracefully
      // Check for various error types and messages
      const errorMessage = String(error?.message || error?.toString() || '').toLowerCase();
      const errorName = String(error?.name || '').toLowerCase();
      const isDeletedError = 
        errorName === 'invalidstateerror' ||
        errorName === 'aborterror' ||
        errorMessage.includes('deleted') ||
        errorMessage.includes('database deleted') ||
        errorMessage.includes('database deleted by request') ||
        errorMessage.includes('database deleted by request of the user') ||
        errorMessage.includes('not found') ||
        error?.code === 0 ||
        String(error).toLowerCase().includes('database deleted by request of the user');
      
      if (isDeletedError) {
        this.initRetryCount += 1;
        if (this.initRetryCount >= this.MAX_INIT_RETRIES) {
          logger.warn('IndexedDB init failed after max retries — running without cache');
        } else {
          logger.warn('IndexedDB was deleted - will reinitialize on next access');
        }
        this.db = null;
        return;
      }
      logger.error('Failed to initialize IndexedDB:', error);
      // Don't throw - caching is optional, app can work without it
      this.db = null; // Reset connection on any error
    }
  }

  /**
   * Save questions to cache
   * 
   * NOTE: This caches the FULL question pool for a test.
   * Adaptive filtering/personalization is applied AFTER loading from cache.
   * 
   * Cache key format: `${testId}-${language}`
   * Example: "traffic-lights-signals-ar" (all traffic lights questions in Arabic)
   */
  async saveQuestions(
    testId: string,
    language: string,
    questions: Question[]
  ): Promise<void> {
    try {
      if (!this.db) await this.init();
      if (!this.db) {
        logger.warn('IndexedDB not available, skipping cache');
        return;
      }

      const key = `${testId}-${language}`;
      await this.db.put('questions', {
        testId,
        language,
        questions,
        timestamp: Date.now()
      }, key);

      logger.debug(`Cached ${questions.length} questions for ${testId} (${language})`);
    } catch (error: any) {
      // Handle database deleted error gracefully - check all variations
      const errorMessage = String(error?.message || error?.toString() || '').toLowerCase();
      const errorName = String(error?.name || '').toLowerCase();
      const isDeletedError = 
        errorName === 'invalidstateerror' ||
        errorName === 'aborterror' ||
        errorMessage.includes('deleted') ||
        errorMessage.includes('database deleted') ||
        errorMessage.includes('database deleted by request') ||
        errorMessage.includes('database deleted by request of the user') ||
        errorMessage.includes('not found') ||
        error?.code === 0 ||
        String(error).toLowerCase().includes('database deleted by request of the user');
      
      if (isDeletedError) {
        logger.warn('IndexedDB was deleted - resetting connection');
        this.db = null;
        // Don't retry - let next call reinitialize
        // Caching is optional, app can work without it
        return;
      }
      logger.error('Failed to save questions to cache:', error);
      // Don't throw - caching is optional
      this.db = null; // Reset connection on persistent errors
    }
  }

  /**
   * Get questions from cache
   * 
   * Returns the FULL question pool for the test.
   * Apply adaptive filtering/personalization AFTER loading.
   */
  async getQuestions(
    testId: string,
    language: string
  ): Promise<Question[] | null> {
    try {
      if (!this.db) await this.init();
      if (!this.db) {
        return null;
      }

      const key = `${testId}-${language}`;
      const data = await this.db.get('questions', key);

      if (!data) {
        return null;
      }

      // Check if cache is fresh (less than expiry days old)
      const age = Date.now() - data.timestamp;
      const maxAge = this.CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

      if (age > maxAge) {
        logger.debug(`Cache expired for ${testId} (${language}), removing`);
        await this.db.delete('questions', key);
        return null;
      }

      logger.debug(`Retrieved ${data.questions.length} cached questions for ${testId} (${language})`);
      return data.questions;
    } catch (error: any) {
      // Handle database deleted error gracefully - check all variations
      const errorMessage = String(error?.message || error?.toString() || '').toLowerCase();
      const errorName = String(error?.name || '').toLowerCase();
      const isDeletedError = 
        errorName === 'invalidstateerror' ||
        errorName === 'aborterror' ||
        errorMessage.includes('deleted') ||
        errorMessage.includes('database deleted') ||
        errorMessage.includes('database deleted by request') ||
        errorMessage.includes('database deleted by request of the user') ||
        errorMessage.includes('not found') ||
        error?.code === 0 ||
        String(error).toLowerCase().includes('database deleted by request of the user');
      
      if (isDeletedError) {
        logger.warn('IndexedDB was deleted - resetting connection');
        this.db = null;
        return null; // Return null to fall back to file loading
      }
      logger.error('Failed to get questions from cache:', error);
      this.db = null; // Reset connection on persistent errors
      return null;
    }
  }

  /**
   * Save individual question (for dynamic mock exams)
   * 
   * Cache key format: `question-${questionId}-${language}`
   */
  async saveQuestion(
    questionId: string,
    language: string,
    question: Question
  ): Promise<void> {
    try {
      if (!this.db) await this.init();
      if (!this.db) return;

      const key = `question-${questionId}-${language}`;
      await this.db.put('questionCache', {
        questionId,
        language,
        question,
        timestamp: Date.now()
      }, key);
    } catch (error: any) {
      // Handle database deleted error gracefully - check all variations
      const errorMessage = String(error?.message || error?.toString() || '').toLowerCase();
      const errorName = String(error?.name || '').toLowerCase();
      const isDeletedError = 
        errorName === 'invalidstateerror' ||
        errorName === 'aborterror' ||
        errorMessage.includes('deleted') ||
        errorMessage.includes('database deleted') ||
        errorMessage.includes('database deleted by request') ||
        errorMessage.includes('database deleted by request of the user') ||
        errorMessage.includes('not found') ||
        error?.code === 0 ||
        String(error).toLowerCase().includes('database deleted by request of the user');
      
      if (isDeletedError) {
        logger.warn('IndexedDB was deleted - resetting connection');
        this.db = null;
        return;
      }
      logger.error('Failed to save individual question:', error);
      this.db = null; // Reset connection on persistent errors
    }
  }

  /**
   * Get individual question (for dynamic mock exams)
   */
  async getQuestion(
    questionId: string,
    language: string
  ): Promise<Question | null> {
    try {
      if (!this.db) await this.init();
      if (!this.db) return null;

      const key = `question-${questionId}-${language}`;
      const data = await this.db.get('questionCache', key);

      if (!data) return null;

      // Check expiry
      const age = Date.now() - data.timestamp;
      const maxAge = this.CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

      if (age > maxAge) {
        await this.db.delete('questionCache', key);
        return null;
      }

      return data.question;
    } catch (error: any) {
      // Handle database deleted error gracefully - check all variations
      const errorMessage = String(error?.message || error?.toString() || '').toLowerCase();
      const errorName = String(error?.name || '').toLowerCase();
      const isDeletedError = 
        errorName === 'invalidstateerror' ||
        errorName === 'aborterror' ||
        errorMessage.includes('deleted') ||
        errorMessage.includes('database deleted') ||
        errorMessage.includes('database deleted by request') ||
        errorMessage.includes('database deleted by request of the user') ||
        errorMessage.includes('not found') ||
        error?.code === 0 ||
        String(error).toLowerCase().includes('database deleted by request of the user');
      
      if (isDeletedError) {
        logger.warn('IndexedDB was deleted - resetting connection');
        this.db = null;
        return null;
      }
      logger.error('Failed to get individual question:', error);
      this.db = null; // Reset connection on persistent errors
      return null;
    }
  }

  async clearCache(): Promise<void> {
    try {
      if (!this.db) await this.init();
      if (!this.db) return;

      await this.db.clear('questions');
      await this.db.clear('questionCache');
      logger.info('Question cache cleared');
    } catch (error: any) {
      // Handle database deleted error gracefully - check all variations
      const errorMessage = String(error?.message || error?.toString() || '').toLowerCase();
      const errorName = String(error?.name || '').toLowerCase();
      const isDeletedError = 
        errorName === 'invalidstateerror' ||
        errorName === 'aborterror' ||
        errorMessage.includes('deleted') ||
        errorMessage.includes('database deleted') ||
        errorMessage.includes('database deleted by request') ||
        errorMessage.includes('database deleted by request of the user') ||
        errorMessage.includes('not found') ||
        error?.code === 0 ||
        String(error).toLowerCase().includes('database deleted by request of the user');
      
      if (isDeletedError) {
        logger.warn('IndexedDB was deleted - resetting connection');
        this.db = null;
        return;
      }
      logger.error('Failed to clear cache:', error);
      this.db = null; // Reset connection on persistent errors
    }
  }

  async clearCacheForTest(testId: string): Promise<void> {
    try {
      if (!this.db) await this.init();
      if (!this.db) return;

      const tx = this.db.transaction('questions', 'readwrite');
      const index = tx.store.index('by-test');
      
      for await (const cursor of index.iterate(testId)) {
        await cursor.delete();
      }

      await tx.done;
      logger.debug(`Cleared cache for test: ${testId}`);
    } catch (error: any) {
      // Handle database deleted error gracefully - check all variations
      const errorMessage = String(error?.message || error?.toString() || '').toLowerCase();
      const errorName = String(error?.name || '').toLowerCase();
      const isDeletedError = 
        errorName === 'invalidstateerror' ||
        errorName === 'aborterror' ||
        errorMessage.includes('deleted') ||
        errorMessage.includes('database deleted') ||
        errorMessage.includes('database deleted by request') ||
        errorMessage.includes('database deleted by request of the user') ||
        errorMessage.includes('not found') ||
        error?.code === 0 ||
        String(error).toLowerCase().includes('database deleted by request of the user');
      
      if (isDeletedError) {
        logger.warn('IndexedDB was deleted - resetting connection');
        this.db = null;
        return;
      }
      logger.error('Failed to clear cache for test:', error);
      this.db = null; // Reset connection on persistent errors
    }
  }

  async getCacheStats(): Promise<{ total: number; size: number; individual: number }> {
    try {
      if (!this.db) await this.init();
      if (!this.db) return { total: 0, size: 0, individual: 0 };

      const all = await this.db.getAll('questions');
      const individual = await this.db.getAll('questionCache');
      const totalSize = JSON.stringify([...all, ...individual]).length;

      return {
        total: all.length,
        individual: individual.length,
        size: totalSize
      };
    } catch (error: any) {
      // Handle database deleted error gracefully - check all variations
      const errorMessage = String(error?.message || error?.toString() || '').toLowerCase();
      const errorName = String(error?.name || '').toLowerCase();
      const isDeletedError = 
        errorName === 'invalidstateerror' ||
        errorName === 'aborterror' ||
        errorMessage.includes('deleted') ||
        errorMessage.includes('database deleted') ||
        errorMessage.includes('database deleted by request') ||
        errorMessage.includes('database deleted by request of the user') ||
        errorMessage.includes('not found') ||
        error?.code === 0 ||
        String(error).toLowerCase().includes('database deleted by request of the user');
      
      if (isDeletedError) {
        logger.warn('IndexedDB was deleted - resetting connection');
        this.db = null;
        return { total: 0, size: 0, individual: 0 };
      }
      logger.error('Failed to get cache stats:', error);
      this.db = null; // Reset connection on persistent errors
      return { total: 0, size: 0, individual: 0 };
    }
  }
}

export const offlineStorage = new OfflineStorageService();
